import React, { useState, useCallback, useMemo, memo } from 'react';
import {
  TextField, Button, Box, Typography, Container,
  InputAdornment, IconButton, Fade, Alert,
  Card, Paper, alpha, useTheme, Dialog,
  DialogTitle, DialogContent, Stack, Stepper,
  Step, StepLabel, useMediaQuery, Divider,
  FormControl, InputLabel, OutlinedInput,
  FormHelperText, Select, MenuItem
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  LockOutlined,
  Visibility,
  VisibilityOff,
  EmailOutlined,
  SecurityOutlined,
  VpnKey,
  PersonOutlined,
  SchoolOutlined
} from '@mui/icons-material';
import api from '../utils/Api';
import { useAuth } from '../contexts/AuthContext';
import { getDefaultDashboardPath } from '../utils/permissions';

// 🎯 Memoized Form Components
const AuthTextField = memo(({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onKeyPress,
  showPassword,
  onTogglePassword,
  size,
  theme,
  isMobile,
  startIcon: StartIcon,
  error,
  helperText,
  required = false
}) => (
  <TextField
    fullWidth
    margin="normal"
    variant="outlined"
    label={label}
    name={name}
    type={type}
    value={value}
    onChange={onChange}
    onKeyPress={onKeyPress}
    size={size}
    error={error}
    helperText={helperText}
    required={required}
    InputProps={{
      startAdornment: StartIcon && (
        <InputAdornment position="start">
          <StartIcon sx={{
            color: error ? 'error.main' : 'primary.main',
            fontSize: isMobile ? '1.2rem' : '1.5rem'
          }} />
        </InputAdornment>
      ),
      endAdornment: onTogglePassword && (
        <InputAdornment position="end">
          <IconButton
            onClick={onTogglePassword}
            edge="end"
            size={size}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      )
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: isMobile ? 2 : 3,
        fontSize: isMobile ? '0.9rem' : '1rem',
        transition: 'all 0.2s ease-in-out',
        '&:hover fieldset': {
          borderColor: error ? 'error.main' : 'primary.main',
          borderWidth: '2px'
        },
        '&.Mui-focused fieldset': {
          borderColor: error ? 'error.main' : 'primary.main',
          borderWidth: '2px',
          boxShadow: error
            ? '0 0 0 4px rgba(244, 67, 54, 0.1)'
            : '0 0 0 4px rgba(102, 126, 234, 0.1)'
        },
        '& fieldset': {
          borderWidth: '2px',
          borderColor: error
            ? alpha(theme.palette.error.main, 0.3)
            : alpha(theme.palette.primary.main, 0.2)
        }
      },
      '& .MuiInputLabel-root': {
        fontWeight: 600,
        fontSize: isMobile ? '0.9rem' : '1rem'
      },
      '& .MuiFormHelperText-root': {
        fontSize: isMobile ? '0.75rem' : '0.875rem'
      }
    }}
  />
));

// 🎯 Memoized Loading Spinner
const LoadingSpinner = memo(({ size = 'medium', text = 'Loading...' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{
      width: size === 'small' ? 14 : size === 'medium' ? 16 : 20,
      height: size === 'small' ? 14 : size === 'medium' ? 16 : 20,
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    {text}
  </Box>
));

// 🎯 Memoized Reset Modal Steps
const ResetStepOne = memo(({
  adminResetEmail,
  setAdminResetEmail,
  resetMessage,
  isResetting,
  onRequestReset,
  isMobile,
  theme
}) => (
  <>
    <Typography
      variant={isMobile ? "caption" : "body2"}
      sx={{
        textAlign: 'center',
        color: 'text.secondary',
        lineHeight: 1.6
      }}
    >
      Enter your admin email address to receive a reset code.
      <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
        <strong>Note:</strong> This reset process is for admin accounts only.
      </Alert>
    </Typography>

    <AuthTextField
      label="Admin Email Address"
      type="email"
      value={adminResetEmail}
      onChange={(e) => setAdminResetEmail(e.target.value)}
      size={isMobile ? "small" : "medium"}
      theme={theme}
      isMobile={isMobile}
      startIcon={EmailOutlined}
    />

    {resetMessage && (
      <Alert
        severity={
          resetMessage.startsWith('✅') || resetMessage.startsWith('🔒')
            ? 'info'
            : resetMessage.startsWith('⚠️')
              ? 'warning'
              : 'error'
        }
        sx={{ borderRadius: 2 }}
      >
        {resetMessage}
      </Alert>
    )}

    <Button
      variant="contained"
      onClick={onRequestReset}
      disabled={isResetting}
      size={isMobile ? "medium" : "large"}
      sx={{
        py: isMobile ? 1.25 : 1.5,
        fontSize: isMobile ? '0.9rem' : '1rem',
        fontWeight: 600,
        borderRadius: isMobile ? 1.5 : 2,
        textTransform: 'none',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        '&:hover': {
          background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        }
      }}
    >
      {isResetting ? (
        <LoadingSpinner
          size={isMobile ? "small" : "medium"}
          text={isMobile ? 'Requesting...' : 'Requesting Code...'}
        />
      ) : (
        'Get Reset Code'
      )}
    </Button>
  </>
));

const ResetStepTwo = memo(({
  adminResetCode,
  setAdminResetCode,
  adminNewPassword,
  setAdminNewPassword,
  adminConfirmPassword,
  setAdminConfirmPassword,
  generatedCode,
  resetMessage,
  isResetting,
  onReset,
  onBack,
  isMobile,
  theme
}) => (
  <>
    <Typography
      variant={isMobile ? "caption" : "body2"}
      sx={{
        textAlign: 'center',
        color: 'text.secondary',
        lineHeight: 1.6
      }}
    >
      Enter the reset code and your new password.
      {generatedCode && (
        <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
          <strong>Your reset code: {generatedCode}</strong> (Expires in 15 minutes)
        </Alert>
      )}
    </Typography>

    <AuthTextField
      label="Reset Code"
      value={adminResetCode}
      onChange={(e) => setAdminResetCode(e.target.value)}
      size={isMobile ? "small" : "medium"}
      theme={theme}
      isMobile={isMobile}
      startIcon={VpnKey}
      placeholder="Enter 6-digit code"
    />

    <AuthTextField
      label="New Password"
      type="password"
      value={adminNewPassword}
      onChange={(e) => setAdminNewPassword(e.target.value)}
      size={isMobile ? "small" : "medium"}
      theme={theme}
      isMobile={isMobile}
      startIcon={LockOutlined}
    />

    <AuthTextField
      label="Confirm New Password"
      type="password"
      value={adminConfirmPassword}
      onChange={(e) => setAdminConfirmPassword(e.target.value)}
      size={isMobile ? "small" : "medium"}
      theme={theme}
      isMobile={isMobile}
      startIcon={LockOutlined}
    />

    {resetMessage && (
      <Alert
        severity={
          resetMessage.startsWith('✅')
            ? 'success'
            : resetMessage.startsWith('⚠️')
              ? 'warning'
              : 'error'
        }
        sx={{ borderRadius: 2 }}
      >
        {resetMessage}
      </Alert>
    )}

    <Box sx={{
      display: 'flex',
      gap: 1,
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      <Button
        variant="outlined"
        onClick={onBack}
        size={isMobile ? "medium" : "large"}
        sx={{
          flex: isMobile ? 'none' : 1,
          py: isMobile ? 1 : 1.5,
          textTransform: 'none',
          borderRadius: isMobile ? 1.5 : 2,
          order: isMobile ? 2 : 1
        }}
      >
        Back
      </Button>
      <Button
        variant="contained"
        onClick={onReset}
        disabled={isResetting}
        size={isMobile ? "medium" : "large"}
        sx={{
          flex: isMobile ? 'none' : 2,
          py: isMobile ? 1 : 1.5,
          fontSize: isMobile ? '0.9rem' : '1rem',
          fontWeight: 600,
          borderRadius: isMobile ? 1.5 : 2,
          textTransform: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
          },
          order: isMobile ? 1 : 2
        }}
      >
        {isResetting ? (
          <LoadingSpinner
            size={isMobile ? "small" : "medium"}
            text={isMobile ? 'Resetting...' : 'Resetting...'}
          />
        ) : (
          'Reset Password'
        )}
      </Button>
    </Box>
  </>
));

// 🎯 Main Auth Component with Combined Login/Register
const AuthPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();

  // 🎯 Mode state - 'login' or 'register'
  const [authMode, setAuthMode] = useState('login');

  // 🎯 Combined form state
  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    schoolName: '',
    schoolType: 'Private', // Default
    confirmPassword: ''
  });

  // 🎯 Validation errors
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Reset Password Modal States
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);

  // Admin reset states
  const [adminResetEmail, setAdminResetEmail] = useState('');
  const [adminResetCode, setAdminResetCode] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('');

  const [resetMessage, setResetMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  // 🎯 Form validation
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (authMode === 'register') {
      if (!form.name.trim()) newErrors.name = 'Name is required';
      if (!form.schoolName.trim()) newErrors.schoolName = 'School name is required';
    }

    if (!form.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!form.password) {
      newErrors.password = 'Password is required';
    } else if (form.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (authMode === 'register') {
      if (form.password !== form.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [authMode, form]);

  // 🎯 Memoized event handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
  }, [errors, apiError]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      setApiError('');

      if (authMode === 'login') {
        console.log("🔍 Sending login data to backend:", form);
        const res = await api.post('/auth/login', form);
        const { user, token } = res.data;

        if (user.role !== 'admin' && user.role !== 'superadmin') {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setApiError('Access denied: only admins can log in here');
          setIsLoading(false);
          return;
        }

        setAuthenticatedUser(user, token);
        navigate(getDefaultDashboardPath(user));
      } else {
        // Register mode
        await api.post('/auth/register', {
          name: form.name,
          email: form.email,
          password: form.password,
          role: 'admin',
          schoolName: form.schoolName,
          schoolType: form.schoolType
        });

        // After successful registration, switch to login mode
        setAuthMode('login');
        setForm(prev => ({
          ...prev,
          name: '',
          schoolName: '',
          schoolType: 'Private',
          confirmPassword: ''
        }));
        setApiError('✅ Registration successful! Please login with your credentials.');
      }
    } catch (err) {
      setApiError(err.response?.data?.message ||
        (authMode === 'login' ? 'Login failed. Please try again.' : 'Registration failed. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [form, authMode, navigate, setAuthenticatedUser, validateForm]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleAuthMode = useCallback(() => {
    setAuthMode(prev => prev === 'login' ? 'register' : 'login');
    setErrors({});
    setApiError('');
    setForm(prev => ({
      ...prev,
      confirmPassword: ''
    }));
  }, []);

  const handleAdminRequestReset = useCallback(async () => {
    if (!adminResetEmail) {
      setResetMessage('⚠️ Please enter your admin email address.');
      return;
    }

    setIsResetting(true);
    setResetMessage('');

    try {
      const response = await api.post('/auth/admin/request-reset', {
        email: adminResetEmail
      });

      setGeneratedCode(response.data.code);
      setResetMessage(`✅ Admin reset code generated: ${response.data.code}. This code expires in 15 minutes.`);
      setResetStep(1);
    } catch (error) {
      if (error.response?.status === 404) {
        setResetMessage(
          '🔒 Admin password reset requires manual process. Please contact system administrator or use the admin panel to generate a reset token.'
        );
      } else {
        const errMsg =
          error.response?.data?.message ||
          '❌ Failed to generate reset code. Please try again or contact administrator.';
        setResetMessage(errMsg);
      }
    } finally {
      setIsResetting(false);
    }
  }, [adminResetEmail]);

  const handleAdminReset = useCallback(async () => {
    if (!adminResetCode || !adminNewPassword || !adminConfirmPassword) {
      setResetMessage('⚠️ Please fill in all fields.');
      return;
    }

    if (adminNewPassword !== adminConfirmPassword) {
      setResetMessage('❌ New passwords do not match.');
      return;
    }

    setIsResetting(true);
    setResetMessage('');

    try {
      await api.post('/auth/reset-password-dob', {
        email: adminResetEmail,
        code: adminResetCode,
        newPassword: adminNewPassword
      });

      setResetMessage('✅ Admin password reset successful! You can now log in with your new password.');

      setTimeout(() => {
        setResetModalOpen(false);
        resetAdminForm();
      }, 3000);
    } catch (error) {
      const errMsg = error.response?.data?.message || '❌ Failed to reset password. Please try again.';
      setResetMessage(errMsg);
    } finally {
      setIsResetting(false);
    }
  }, [adminResetCode, adminNewPassword, adminConfirmPassword, adminResetEmail]);

  const resetAdminForm = useCallback(() => {
    setResetStep(0);
    setAdminResetEmail('');
    setAdminResetCode('');
    setAdminNewPassword('');
    setAdminConfirmPassword('');
    setResetMessage('');
    setGeneratedCode('');
  }, []);

  const handleCloseResetModal = useCallback(() => {
    setResetModalOpen(false);
    resetAdminForm();
  }, [resetAdminForm]);

  const handleBackStep = useCallback(() => {
    setResetStep(0);
  }, []);

  // 🎯 Memoized computed values
  const adminResetSteps = useMemo(() =>
    ['Enter Admin Email', 'Enter Reset Code & New Password'],
    []
  );

  const headerContent = useMemo(() => (
    <Box sx={{ textAlign: 'center', mb: isMobile ? 3 : 4 }}>
      <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: isMobile ? 60 : 80,
        height: isMobile ? 60 : 80,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.4)',
        mb: isMobile ? 2 : 3
      }}>
        {authMode === 'login' ? (
          <SecurityOutlined sx={{
            fontSize: isMobile ? 30 : 40,
            color: 'white'
          }} />
        ) : (
          <SchoolOutlined sx={{
            fontSize: isMobile ? 30 : 40,
            color: 'white'
          }} />
        )}
      </Box>

      <Typography
        variant={isMobile ? "h4" : "h3"}
        component="h1"
        sx={{
          fontWeight: 800,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          letterSpacing: '-0.5px',
          mb: 1,
          fontSize: isMobile ? '1.75rem' : '2.5rem'
        }}
      >
        {authMode === 'login' ? 'Admin Portal' : 'Register School'}
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          fontSize: isMobile ? '0.9rem' : '1.1rem',
          fontWeight: 500
        }}
      >
        {authMode === 'login'
          ? 'Secure access to your dashboard'
          : 'Create your school and admin account'}
      </Typography>
    </Box>
  ), [isMobile, authMode]);

  const errorAlert = useMemo(() =>
    apiError ? (
      <Alert
        severity={apiError.startsWith('✅') ? "success" : "error"}
        sx={{
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: apiError.startsWith('✅') ? 'success.light' : 'error.light',
          bgcolor: apiError.startsWith('✅')
            ? alpha(theme.palette.success.main, 0.05)
            : alpha(theme.palette.error.main, 0.05),
          '& .MuiAlert-icon': {
            color: apiError.startsWith('✅') ? 'success.main' : 'error.main'
          }
        }}
      >
        {apiError}
      </Alert>
    ) : null,
    [apiError, theme]
  );

  const securityNote = useMemo(() => (
    <Paper sx={{
      mt: isMobile ? 3 : 4,
      p: isMobile ? 1.5 : 2,
      borderRadius: isMobile ? 1.5 : 2,
      bgcolor: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      textAlign: 'center'
    }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
          fontSize: isMobile ? '0.75rem' : '0.875rem'
        }}
      >
        🔒 {authMode === 'login'
          ? 'Secure admin access only. All activities are logged and monitored.'
          : 'Your school data is securely stored and encrypted.'}
      </Typography>
    </Paper>
  ), [isMobile, theme, authMode]);

  const resetModalContent = useMemo(() => (
    <Dialog
      open={resetModalOpen}
      onClose={handleCloseResetModal}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          p: isMobile ? 1 : 2,
          background: 'rgba(255, 255, 255, 0.98)',
          backdropFilter: 'blur(20px)',
          m: isMobile ? 0 : 'auto',
          maxHeight: isMobile ? '100%' : 'auto'
        }
      }}
    >
      <DialogTitle sx={{
        textAlign: 'center',
        fontWeight: 700,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontSize: isMobile ? '1.25rem' : '1.5rem',
        pb: 1,
        pt: isMobile ? 2 : 1
      }}>
        Reset Admin Password
      </DialogTitle>

      <DialogContent>
        <Stepper
          activeStep={resetStep}
          sx={{
            mb: isMobile ? 3 : 4,
            '& .MuiStepLabel-root': {
              padding: isMobile ? '8px' : '16px'
            }
          }}
        >
          {adminResetSteps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }
                }}
              >
                {isMobile ? label.split(' ')[0] + '...' : label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack spacing={isMobile ? 2 : 3}>
          {resetStep === 0 ? (
            <ResetStepOne
              adminResetEmail={adminResetEmail}
              setAdminResetEmail={setAdminResetEmail}
              resetMessage={resetMessage}
              isResetting={isResetting}
              onRequestReset={handleAdminRequestReset}
              isMobile={isMobile}
              theme={theme}
            />
          ) : (
            <ResetStepTwo
              adminResetCode={adminResetCode}
              setAdminResetCode={setAdminResetCode}
              adminNewPassword={adminNewPassword}
              setAdminNewPassword={setAdminNewPassword}
              adminConfirmPassword={adminConfirmPassword}
              setAdminConfirmPassword={setAdminConfirmPassword}
              generatedCode={generatedCode}
              resetMessage={resetMessage}
              isResetting={isResetting}
              onReset={handleAdminReset}
              onBack={handleBackStep}
              isMobile={isMobile}
              theme={theme}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  ), [
    resetModalOpen, resetStep, adminResetSteps, adminResetEmail, adminResetCode,
    adminNewPassword, adminConfirmPassword, generatedCode, resetMessage, isResetting,
    isMobile, theme, handleAdminRequestReset, handleAdminReset, handleBackStep, handleCloseResetModal
  ]);

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      py: isMobile ? 2 : 0,
      px: isMobile ? 1 : 0,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 30% 20%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 119, 198, 0.2) 0%, transparent 50%)',
        animation: 'float 6s ease-in-out infinite'
      },
      '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-20px)' }
      }
    }}>
      <Container
        maxWidth="sm"
        sx={{
          position: 'relative',
          zIndex: 1,
          px: isMobile ? 1 : 2
        }}
      >
        <Fade in timeout={800}>
          <Card sx={{
            p: isMobile ? 2 : isTablet ? 4 : 5,
            borderRadius: isMobile ? 2 : 4,
            boxShadow: isMobile
              ? '0 16px 32px -8px rgba(0, 0, 0, 0.2)'
              : '0 32px 64px -12px rgba(0, 0, 0, 0.25), 0 0 32px -8px rgba(255, 255, 255, 0.2)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.4)',
            position: 'relative',
            overflow: 'hidden',
            mx: isMobile ? 1 : 0,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
            }
          }}>
            {headerContent}
            {errorAlert}

            {/* Form Fields */}
            <Box sx={{ mb: 3 }}>
              {authMode === 'register' && (
                <>
                  <AuthTextField
                    label="Full Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    size={isMobile ? "small" : "medium"}
                    theme={theme}
                    isMobile={isMobile}
                    startIcon={PersonOutlined}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                  />

                  <AuthTextField
                    label="School Name"
                    name="schoolName"
                    value={form.schoolName}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    size={isMobile ? "small" : "medium"}
                    theme={theme}
                    isMobile={isMobile}
                    startIcon={SchoolOutlined}
                    error={!!errors.schoolName}
                    helperText={errors.schoolName}
                    required
                  />

                  <FormControl fullWidth margin="normal">
                    <InputLabel id="school-type-label">School Type</InputLabel>
                    <Select
                      labelId="school-type-label"
                      name="schoolType"
                      value={form.schoolType}
                      label="School Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="Private">Private</MenuItem>
                      <MenuItem value="Government">Government (Basic)</MenuItem>
                    </Select>
                  </FormControl>


                </>
              )}


              <AuthTextField
                label="Email Address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                size={isMobile ? "small" : "medium"}
                theme={theme}
                isMobile={isMobile}
                startIcon={EmailOutlined}
                error={!!errors.email}
                helperText={errors.email}
                required
              />

              <AuthTextField
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                size={isMobile ? "small" : "medium"}
                theme={theme}
                isMobile={isMobile}
                startIcon={LockOutlined}
                showPassword={showPassword}
                onTogglePassword={togglePasswordVisibility}
                error={!!errors.password}
                helperText={errors.password || (authMode === 'login' ? '' : 'Minimum 8 characters')}
                required
              />

              {authMode === 'register' && (
                <AuthTextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  size={isMobile ? "small" : "medium"}
                  theme={theme}
                  isMobile={isMobile}
                  startIcon={LockOutlined}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  required
                />
              )}
            </Box>

            {/* Submit Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              size={isMobile ? "medium" : "large"}
              sx={{
                py: isMobile ? 1.5 : 1.8,
                fontSize: isMobile ? '1rem' : '1.1rem',
                fontWeight: 700,
                borderRadius: isMobile ? 2 : 3,
                textTransform: 'none',
                letterSpacing: '0.5px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: isMobile
                  ? '0 4px 16px rgba(102, 126, 234, 0.3)'
                  : '0 8px 32px rgba(102, 126, 234, 0.4)',
                transition: 'all 0.3s ease-in-out',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: isMobile
                    ? '0 6px 20px rgba(102, 126, 234, 0.5)'
                    : '0 12px 40px rgba(102, 126, 234, 0.6)',
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                  transition: 'left 0.5s'
                },
                '&:hover::before': {
                  left: '100%'
                }
              }}
            >
              {isLoading ? (
                <LoadingSpinner
                  size={isMobile ? "medium" : "large"}
                  text={isMobile ?
                    (authMode === 'login' ? 'Signing In...' : 'Creating...')
                    : (authMode === 'login' ? 'Signing In...' : 'Creating Account...')
                  }
                />
              ) : (
                authMode === 'login'
                  ? (isMobile ? 'Sign In' : 'Sign In to Dashboard')
                  : (isMobile ? 'Create Account' : 'Create School Account')
              )}
            </Button>

            {/* Footer Links */}
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              {authMode === 'login' && (
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  onClick={() => setResetModalOpen(true)}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                    '&:hover': {
                      color: 'secondary.main',
                      transform: 'translateX(4px)'
                    }
                  }}
                >
                  Forgot your password?
                  <Box component="span" sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>→</Box>
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography
                variant={isMobile ? "caption" : "body2"}
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                {authMode === 'login'
                  ? "Don't have an account? "
                  : "Already have an account? "}
                <Typography
                  component="span"
                  onClick={toggleAuthMode}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      color: 'secondary.main',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {authMode === 'login' ? 'Create one' : 'Sign in'}
                </Typography>
              </Typography>
            </Box>

            {securityNote}
          </Card>
        </Fade>

        {resetModalContent}

        {/* Global Styles for Animations */}
        <style jsx global>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </Container>
    </Box >
  );
};

// 🎯 Export memoized component
export default memo(AuthPage);
